import React, { useEffect } from 'react'
import {
    TreeView,
    TreeNode,
    Checkbox,
    OverflowMenu,
    OverflowMenuItem,
} from '@carbon/react';
import { ChevronDown } from '@carbon/icons-react'
import fileSize from 'filesize';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    loadIngestLocationContent,
    loadIngestLocations,
    selectIngestContent,
    selectIngestLocations,
    selectSelectedIngestLocation,
    selectSelectedIngestPath,
    selectToken,
    setSelectedIngestLocation,
    setSelectedIngestPath,
} from '../../../AppSlice';

interface ITreeNodeItem {
    id: string;
    label: string;
}

interface INodeProps {
    item: ITreeNodeItem;
    selected: string[];
}

function IngestTreeNode({ item, selected }: INodeProps) {
    const dispatch = useAppDispatch()
    const children = useAppSelector(selectIngestContent)[item.id] || [];

    const onSelect = () => {
        dispatch(setSelectedIngestPath(item.id))
    }

    return (
        <TreeNode
            id={item.id}
            label={item.label}
            selected={selected}
            isExpanded
            onSelect={onSelect}
            onClick={onSelect}
        >
            {children
                .filter(child => child.isDirectory)
                .map(child => (
                    <IngestTreeNode
                        key={item.id + child.relativePath}
                        selected={selected}
                        item={{ id: child.relativePath, label: child.name }}
                    />
                ))}
        </TreeNode>
    )
}

interface IProps {
    selectedFiles: string[];
    onSelectedFilesChange: (files: string[]) => void;
}

export default function IngestTree({ selectedFiles, onSelectedFilesChange }: IProps) {

    const dispatch = useAppDispatch()
    const token = useAppSelector(selectToken)
    const locations = useAppSelector(selectIngestLocations)
    const selectedLocation = useAppSelector(selectSelectedIngestLocation)
    const selectedPath = useAppSelector(selectSelectedIngestPath)
    const content = useAppSelector(selectIngestContent)

    useEffect(() => {
        if (token) dispatch(loadIngestLocations({ token }))
    }, [dispatch, token])

    useEffect(() => {
        if (token && selectedLocation) {
            dispatch(loadIngestLocationContent({ token, locationId: selectedLocation, path: selectedPath }))
        }
    }, [dispatch, selectedLocation, selectedPath, token])

    const onLocationChanged = (locationId: string) => {
        dispatch(setSelectedIngestLocation(locationId))
        dispatch(setSelectedIngestPath("/"))
        onSelectedFilesChange([])
    }

    const currentFiles = (content[selectedPath ?? "root"] ?? []).filter(f => !f.isDirectory)

    const toggleFile = (relativePath: string, checked: boolean) => {
        if (checked) {
            onSelectedFilesChange([...selectedFiles, relativePath])
        } else {
            onSelectedFilesChange(selectedFiles.filter(f => f !== relativePath))
        }
    }

    return (
        <div style={{ display: 'flex', gap: 12, border: '1px solid #333', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ width: '35%', borderRight: '1px solid #333' }}>
                <div style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid #333' }}>
                    <span>Location</span>
                    <OverflowMenu aria-label="ingest-location-menu" size="sm" renderIcon={ChevronDown}>
                        <OverflowMenuItem
                            style={!selectedLocation ? { backgroundColor: "rgba(255,255,0,0.3)" } : {}}
                            itemText="Choose..."
                            onClick={() => dispatch(setSelectedIngestLocation(null))}
                        />
                        {locations?.map(location => (
                            <OverflowMenuItem
                                key={"ingest-loc-" + location.id}
                                style={selectedLocation === location.id ? { backgroundColor: "rgba(255,255,0,0.3)" } : {}}
                                onClick={() => onLocationChanged(location.id)}
                                itemText={location.label}
                            />
                        ))}
                    </OverflowMenu>
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto', padding: 8 }}>
                    {selectedLocation ? (
                        <TreeView
                            label=""
                            key={"ingest-tree-" + selectedLocation}
                            selected={[selectedPath ?? selectedLocation]}
                            onSelect={(e: any) => dispatch(setSelectedIngestPath(e.currentTarget.nodeValue))}
                        >
                            <TreeNode
                                id={selectedLocation}
                                label={locations.find(l => l.id === selectedLocation)?.label ?? selectedLocation}
                                isExpanded
                                onSelect={() => dispatch(setSelectedIngestPath("/"))}
                                onClick={() => dispatch(setSelectedIngestPath("/"))}
                            >
                                {(content["root"] ?? [])
                                    .filter(f => f.isDirectory)
                                    .map(f => (
                                        <IngestTreeNode
                                            key={selectedLocation + f.relativePath}
                                            selected={[selectedPath]}
                                            item={{ id: f.relativePath, label: f.name }}
                                        />
                                    ))}
                            </TreeNode>
                        </TreeView>
                    ) : (
                        <div style={{ fontSize: 12, color: '#888', padding: 6 }}>Select a storage location to browse.</div>
                    )}
                </div>
            </div>
            <div style={{ flex: 1, padding: 8 }}>
                <div style={{ fontSize: 12, color: '#c6c6c6', marginBottom: 6 }}>
                    Folder: <strong>{selectedPath && selectedPath !== "/" ? selectedPath : "Root"}</strong>
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {currentFiles.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#888' }}>No files in this folder.</div>
                    ) : currentFiles.map(file => (
                        <div key={file.relativePath} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <Checkbox
                                id={`ingest-file-${file.relativePath}`}
                                labelText={file.name}
                                checked={selectedFiles.includes(file.relativePath)}
                                onChange={(_e, { checked }) => toggleFile(file.relativePath, Boolean(checked))}
                            />
                            <span style={{ fontSize: 11, color: '#888' }}>{fileSize(file.size ?? 0)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
